import React from "react";
import { observer } from "mobx-react";
import { Grid, Box } from "@mui/material";
import { useRootStore } from "../../../../store/contexts";
import { GroupContainer } from "../../../../common/components";
import { FormToolbox } from "../../../../common/controls/forms/FormToolbox";
import { FormSaveFooter } from "../../../../common/controls/forms/FormSaveFooter";

import FormExtras from "../../../../common/components/formExtras";
import { TenantDocumentCreateStore } from "../../store";
import { InputString } from "../../../common/controls";
import { InputText } from "../../../common/controls";
import { FileCRUDType } from "../../../common/types";
import { GridChangeSet } from "../../../common/types";
import { LoadFilesCRUDCompact } from "../../../common/controls";
import { ChunkingStrategy } from "../../../../api/graphql";
import { InputReference } from "../../../common/controls";
import { InputNumber } from "../../../common/controls";
import { FormControl } from "@mui/material";

export type TenantDocumentCreateFormProps = {
  onSave?: () => void;
  onCancel?: () => void;
  /** Mientras se guarda, Guardar queda deshabilitado. */
  saving?: boolean;
  showToolbox?: boolean;
  showSaveFooter?: boolean;
  nameSelectedValue?: { displayId: string; id: string } | null;
  onNameChange?: (value: string) => void;
  name: string;
  nameError?: string;
  descriptionSelectedValue?: { displayId: string; id: string } | null;
  onDescriptionChange?: (value: string) => void;
  description: string;
  descriptionError?: string;
  fileOnRestoreFile: (id: string) => void;
  fileOnLoadFile: (file: any) => void;
  fileOnCommitFile: (changes: GridChangeSet) => void;
  fileFiles: FileCRUDType[];
  fileError?: string;
  processingErrorSelectedValue?: { displayId: string; id: string } | null;
  onProcessingErrorChange?: (value: string) => void;
  processingError: string;
  processingErrorError?: string;
  contentTypeSelectedValue?: { displayId: string; id: string } | null;
  onContentTypeChange?: (value: string) => void;
  contentType: string;
  contentTypeError?: string;
  chunkingStrategyError?: string;
  chunkingStrategyOptions: { id: string; displayId: string }[];
  chunkingStrategy: ChunkingStrategy;
  onChunkingStrategyChange?: (value: ChunkingStrategy) => void;
  chunkingSeparatorRegexSelectedValue?: {
    displayId: string;
    id: string;
  } | null;
  onChunkingSeparatorRegexChange?: (value: string) => void;
  chunkingSeparatorRegex: string;
  chunkingSeparatorRegexError?: string;
  chunkingMaxTokensSelectedValue?: { displayId: string; id: string } | null;
  onChunkingMaxTokensChange?: (value: number | undefined) => void;
  chunkingMaxTokens: number | undefined;
  chunkingMaxTokensError?: string;
  chunkingOverlapTokensSelectedValue?: { displayId: string; id: string } | null;
  onChunkingOverlapTokensChange?: (value: number | undefined) => void;
  chunkingOverlapTokens: number | undefined;
  chunkingOverlapTokensError?: string;
  dummyCRUDRestoredObservable: number;
  tenantDocumentCreateStore: TenantDocumentCreateStore;
};

export const TenantDocumentCreateForm: React.FC<TenantDocumentCreateFormProps> =
  observer(
    ({
      onSave = () => {},
      onCancel = () => {},
      saving = false,
      showToolbox = true,
      showSaveFooter,
      name = "",
      nameError,
      onNameChange = () => {},
      description = "",
      descriptionError,
      onDescriptionChange = () => {},
      fileOnRestoreFile,
      fileOnLoadFile,
      fileOnCommitFile,
      fileFiles,
      fileError,
      processingError = "",
      processingErrorError,
      onProcessingErrorChange = () => {},
      contentType = "",
      contentTypeError,
      onContentTypeChange = () => {},
      chunkingStrategyError,
      chunkingStrategyOptions = [],
      chunkingStrategy = "" as ChunkingStrategy,
      onChunkingStrategyChange = () => {},
      chunkingSeparatorRegex = "",
      chunkingSeparatorRegexError,
      onChunkingSeparatorRegexChange = () => {},
      chunkingMaxTokens = undefined,
      chunkingMaxTokensError,
      onChunkingMaxTokensChange = () => {},
      chunkingOverlapTokens = undefined,
      chunkingOverlapTokensError,
      onChunkingOverlapTokensChange = () => {},
      dummyCRUDRestoredObservable,
      tenantDocumentCreateStore,
    }) => {
      const rootStore = useRootStore();
      const { langStore } = rootStore;

      return (
        <Grid>
          {showToolbox && (
            <FormToolbox
              saveAndCloseLabel={langStore.saveEntity}
              closeLabel={langStore.cancelNewEntity}
              saveForm={() => onSave()}
              saveDisabled={saving}
              closeForm={() => onCancel()}
            />
          )}
          <Box>
            <FormExtras
              entity="TenantDocument"
              mode="create"
              values={{}}
              options={{}}
              onPatch={(campo: string, valor: any) => {
                switch (campo) {
                  case "name":
                    onNameChange?.(valor);
                    break;
                  case "description":
                    onDescriptionChange?.(valor);
                    break;
                  case "processingError":
                    onProcessingErrorChange?.(valor);
                    break;
                  case "contentType":
                    onContentTypeChange?.(valor);
                    break;
                  case "chunkingStrategy":
                    onChunkingStrategyChange?.(valor);
                    break;
                  case "chunkingSeparatorRegex":
                    onChunkingSeparatorRegexChange?.(valor);
                    break;
                  case "chunkingMaxTokens":
                    onChunkingMaxTokensChange?.(valor);
                    break;
                  case "chunkingOverlapTokens":
                    onChunkingOverlapTokensChange?.(valor);
                    break;
                }
              }}
            />
            <GroupContainer
              id="null"
              errorText={tenantDocumentCreateStore.getCardErrors("null")}
              title={langStore.groupTenantDocumentLabel}
              collapsible={false}
            >
              <FormControl fullWidth>
                <InputString
                  autoFocus
                  value={name}
                  error={Boolean(nameError)}
                  warning={
                    !nameError &&
                    Boolean(tenantDocumentCreateStore.warnings["name"])
                  }
                  helperText={
                    nameError ||
                    tenantDocumentCreateStore.warnings["name"]?.message
                  }
                  label={langStore.TenantDocumentNameLabel}
                  onChange={(value: string) => onNameChange(value)}
                  description={langStore.TenantDocumentNameDescription}
                />

                <InputText
                  value={description}
                  error={Boolean(descriptionError)}
                  warning={
                    !descriptionError &&
                    Boolean(tenantDocumentCreateStore.warnings["description"])
                  }
                  helperText={
                    descriptionError ||
                    tenantDocumentCreateStore.warnings["description"]?.message
                  }
                  label={langStore.TenantDocumentDescriptionLabel}
                  onChange={(value: string) => onDescriptionChange(value)}
                  nullText={langStore.optionalFieldHint}
                  description={langStore.TenantDocumentDescriptionDescription}
                />
                <LoadFilesCRUDCompact
                  rows={fileFiles}
                  maxRows={1}
                  showAddCommand={true}
                  showDeleteCommand={true}
                  label={langStore.TenantDocumentFileLabel}
                  description={langStore.TenantDocumentFileDescription}
                  showLabel={true}
                  onLoadFile={(file: File) => fileOnLoadFile(file)}
                  columns={[
                    { name: "mimetype", title: langStore.fileTypeLabel },
                    { name: "name", title: langStore.fileNameLabel },
                    { name: "formattedSize", title: langStore.fileSizeLabel },
                  ]}
                  onRestore={(id: string) => fileOnRestoreFile(id)}
                  onCommitChanges={(changes: GridChangeSet) =>
                    fileOnCommitFile(changes)
                  }
                  dummyCRUDRestoredObservable={dummyCRUDRestoredObservable}
                  error={fileError}
                />

                <InputString
                  value={processingError}
                  error={Boolean(processingErrorError)}
                  warning={
                    !processingErrorError &&
                    Boolean(
                      tenantDocumentCreateStore.warnings["processingError"]
                    )
                  }
                  helperText={
                    processingErrorError ||
                    tenantDocumentCreateStore.warnings["processingError"]
                      ?.message
                  }
                  label={langStore.TenantDocumentProcessingErrorLabel}
                  onChange={(value: string) => onProcessingErrorChange(value)}
                  nullText={langStore.optionalFieldHint}
                  description={
                    langStore.TenantDocumentProcessingErrorDescription
                  }
                />

                <InputString
                  value={contentType}
                  error={Boolean(contentTypeError)}
                  warning={
                    !contentTypeError &&
                    Boolean(tenantDocumentCreateStore.warnings["contentType"])
                  }
                  helperText={
                    contentTypeError ||
                    tenantDocumentCreateStore.warnings["contentType"]?.message
                  }
                  label={langStore.TenantDocumentContentTypeLabel}
                  onChange={(value: string) => onContentTypeChange(value)}
                  nullText={langStore.optionalFieldHint}
                  description={langStore.TenantDocumentContentTypeDescription}
                />
                <InputReference
                  id="chunkingStrategy"
                  error={Boolean(chunkingStrategyError)}
                  helperText={
                    chunkingStrategyError ||
                    tenantDocumentCreateStore.warnings["chunkingStrategy"]
                      ?.message
                  }
                  label={langStore.TenantDocumentChunkingStrategyLabel}
                  options={chunkingStrategyOptions}
                  selectedValue={
                    chunkingStrategy
                      ? {
                          id: chunkingStrategy,
                          displayId:
                            chunkingStrategyOptions.find(
                              (o: { id: string }) => o.id === chunkingStrategy
                            )?.displayId || chunkingStrategy,
                        }
                      : null
                  }
                  onChange={() => {}}
                  onSelect={(value: { id: string; displayId: string } | null) =>
                    onChunkingStrategyChange(
                      (value?.id as ChunkingStrategy) ||
                        ("" as ChunkingStrategy)
                    )
                  }
                />

                <InputString
                  value={chunkingSeparatorRegex}
                  error={Boolean(chunkingSeparatorRegexError)}
                  warning={
                    !chunkingSeparatorRegexError &&
                    Boolean(
                      tenantDocumentCreateStore.warnings[
                        "chunkingSeparatorRegex"
                      ]
                    )
                  }
                  helperText={
                    chunkingSeparatorRegexError ||
                    tenantDocumentCreateStore.warnings["chunkingSeparatorRegex"]
                      ?.message
                  }
                  label={langStore.TenantDocumentChunkingSeparatorRegexLabel}
                  onChange={(value: string) =>
                    onChunkingSeparatorRegexChange(value)
                  }
                  nullText={langStore.optionalFieldHint}
                  description={
                    langStore.TenantDocumentChunkingSeparatorRegexDescription
                  }
                />
                <InputNumber
                  value={chunkingMaxTokens}
                  nullable={true}
                  error={Boolean(chunkingMaxTokensError)}
                  warning={
                    !chunkingMaxTokensError &&
                    Boolean(
                      tenantDocumentCreateStore.warnings["chunkingMaxTokens"]
                    )
                  }
                  helperText={
                    chunkingMaxTokensError ||
                    tenantDocumentCreateStore.warnings["chunkingMaxTokens"]
                      ?.message
                  }
                  allowNegative={false}
                  label={langStore.TenantDocumentChunkingMaxTokensLabel}
                  decimalScale={0}
                  nullText={langStore.optionalFieldHint}
                  description={
                    langStore.TenantDocumentChunkingMaxTokensDescription
                  }
                  onChange={(value: number | undefined) =>
                    onChunkingMaxTokensChange(value)
                  }
                />
                <InputNumber
                  value={chunkingOverlapTokens}
                  nullable={true}
                  error={Boolean(chunkingOverlapTokensError)}
                  warning={
                    !chunkingOverlapTokensError &&
                    Boolean(
                      tenantDocumentCreateStore.warnings[
                        "chunkingOverlapTokens"
                      ]
                    )
                  }
                  helperText={
                    chunkingOverlapTokensError ||
                    tenantDocumentCreateStore.warnings["chunkingOverlapTokens"]
                      ?.message
                  }
                  allowNegative={false}
                  label={langStore.TenantDocumentChunkingOverlapTokensLabel}
                  decimalScale={0}
                  nullText={langStore.optionalFieldHint}
                  description={
                    langStore.TenantDocumentChunkingOverlapTokensDescription
                  }
                  onChange={(value: number | undefined) =>
                    onChunkingOverlapTokensChange(value)
                  }
                />
              </FormControl>
            </GroupContainer>
          </Box>
          {(showSaveFooter ?? !showToolbox) && (
            <FormSaveFooter
              onSave={() => onSave()}
              label={langStore.saveEntity}
              disabled={saving}
            />
          )}
        </Grid>
      );
    }
  );
